import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { Lock, Crown, Clock } from 'lucide-react';
import { Button } from '../ui/button';

export default function SubscriptionGate({ children, featureName = 'this feature' }) {
  const { hasAccess, loading, isTrial, daysRemaining, status } = useSubscription();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    const isTrialExpired = isTrial && daysRemaining === 0;
    
    return (
      <div className="min-h-[600px] flex items-center justify-center p-8">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              {isTrialExpired ? (
                <Clock className="w-12 h-12 text-white" />
              ) : (
                <Lock className="w-12 h-12 text-white" />
              )}
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
              {isTrialExpired ? 'Your Trial Has Ended' : 'Premium Feature'}
            </h2>
            
            <p className="text-lg text-slate-300 mb-2">
              {isTrialExpired 
                ? `Your 7-day trial has expired. Subscribe to continue accessing ${featureName}.`
                : `Access to ${featureName} requires an active subscription.`
              }
            </p>
            
            {isTrialExpired && (
              <p className="text-sm text-slate-400">
                Thank you for trying CorpCraft! Upgrade now to unlock all premium features.
              </p>
            )}
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">Unlock Premium Features:</h3>
            <ul className="text-left text-slate-300 space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>UCC Filings & State Compliance</span>
              </li>
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>Trademark Registration & IP Registry</span>
              </li>
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>Credit Intelligence & Fundability Scoring</span>
              </li>
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>Investor Diligence Reports & API Access</span>
              </li>
              <li className="flex items-start gap-3">
                <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>Compliance Dashboard & Audit Logging</span>
              </li>
            </ul>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full gradient-accent text-lg py-6"
            >
              <Crown className="w-5 h-5 mr-2" />
              View Pricing Plans
            </Button>
          </div>

          <p className="text-sm text-slate-500">
            Need help? Contact support at support@corpcraftlegal.com
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
